import React, { useMemo } from 'react';
import styles from './CalendarCustomizeContent.module.scss';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import clx from 'classnames';
import { ChevronDown } from 'lucide-react';

import { sameChairId } from './sameChairId';

const TIME_STEP_MINUTES = 30;

const padTime = (hours, minutes) =>
  `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

const ALL_TIME_SLOTS = Array.from({ length: (24 * 60) / TIME_STEP_MINUTES }, (_, index) => {
  const total = index * TIME_STEP_MINUTES;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return { hours, minutes, label: padTime(hours, minutes) };
});

const parseWorkTime = (workTime) =>
  dayjs(`${workTime?.hours}:${workTime?.minutes}`, 'HH:mm');

const TimeSelect = ({ label, slots, selected, onSelect }) => {
  const selectedLabel = padTime(selected?.hours, selected?.minutes);
  const value = slots.some((slot) => slot.label === selectedLabel)
    ? selectedLabel
    : slots[0]?.label || '';

  return (
    <label className={styles.time_select}>
      <span className={styles.time_select_label}>{label}</span>
      <div className={styles.time_select_field}>
        <select
          value={value}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            const slot = slots.find((item) => item.label === event.target.value);
            if (slot) onSelect(slot);
          }}
        >
          {slots.map((slot) => (
            <option key={slot.label} value={slot.label}>
              {slot.label}
            </option>
          ))}
        </select>
        <ChevronDown size={16} strokeWidth={2.2} />
      </div>
    </label>
  );
};

const CalendarCustomizeContent = ({
  calendarWorkers,
  activeWorkerId,
  onActiveWorkerChange,
  daySelectedValues,
  numberOfActiveDays,
  onNumberOfActiveDaysChange,
  stepSize,
  onStepSizeChange,
  startWorkTime,
  endWorkTime,
  onStartWorkTimeChange,
  onEndWorkTimeChange,
  searchParams,
  showWorkerSelector = true,
  hideClosedChairs = false,
  onHideClosedChairsChange,
  compactClosedDays = false,
  onCompactClosedDaysChange,
  showTaskIcons = true,
  onShowTaskIconsChange,
  showTaskTime = true,
  onShowTaskTimeChange,
}) => {
  const { t } = useTranslation();

  const showWorkers =
    showWorkerSelector && (searchParams?.get('viewJustMe') == 0 || !localStorage.getItem('staff'));

  const displayedEnd = parseWorkTime(endWorkTime).subtract(30, 'minute');
  const displayedEndValue = {
    hours: displayedEnd.$H,
    minutes: displayedEnd.$m,
  };

  const startSlots = useMemo(() => {
    const maxStart = endWorkTime?.hours
      ? dayjs(`${endWorkTime.hours - 5}:${endWorkTime.minutes}`, 'HH:mm')
      : dayjs('22:00', 'HH:mm');
    return ALL_TIME_SLOTS.filter((slot) => !dayjs(slot.label, 'HH:mm').isAfter(maxStart));
  }, [endWorkTime?.hours, endWorkTime?.minutes]);

  const endSlots = useMemo(() => {
    const minEnd = parseWorkTime(startWorkTime);
    return ALL_TIME_SLOTS.filter((slot) => !dayjs(slot.label, 'HH:mm').isBefore(minEnd));
  }, [startWorkTime?.hours, startWorkTime?.minutes]);

  const handleStartSelect = (slot) => {
    onStartWorkTimeChange({
      hours: slot.hours,
      minutes: slot.minutes,
    });
  };

  const handleEndSelect = (slot) => {
    const nextValue = dayjs(slot.label, 'HH:mm').add(30, 'minute');
    onEndWorkTimeChange({
      hours: nextValue.$H ? nextValue.$H : 24,
      minutes: nextValue.$m,
    });
  };

  return (
    <div className={styles.customize_content}>
      {showWorkers && (
        <section className={styles.section}>
          <div className={styles.section_label}>{t('scheduler.calendarHeaderModal.chairs')}</div>
          <div className={styles.day_chips} role="listbox" aria-label={t('scheduler.calendarHeaderModal.chairs')}>
            {(calendarWorkers || []).map((option) => {
              const isActive = sameChairId(option.value, activeWorkerId);
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={clx(styles.day_chip, {
                    [styles.day_chip_active]: isActive,
                  })}
                  onClick={() => onActiveWorkerChange(option.value)}
                >
                  {option.name}
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.section_label}>{t('scheduler.calendarHeaderModal.view')}</div>
        <div className={styles.day_chips}>
          {(daySelectedValues || []).map((option) => (
            <button
              key={option.value}
              type="button"
              className={clx(styles.day_chip, {
                [styles.day_chip_active]: option.value === Number(numberOfActiveDays),
              })}
              onClick={() => onNumberOfActiveDaysChange(option.value)}
            >
              {option.name}
            </button>
          ))}
        </div>
      </section>

      {!!stepSize && (
        <>
          <section className={styles.section}>
            <div className={styles.section_label}>{t('scheduler.calendarHeaderModal.calendarZoom')}</div>
            <div className={styles.zoom_row}>
              <span>{t('scheduler.calendarHeaderModal.zoomCompact')}</span>
              <input
                className={styles.zoom_range}
                type="range"
                min="7"
                max="24"
                value={stepSize}
                onChange={(e) => onStepSizeChange(+e.target.value)}
              />
              <span>{t('scheduler.calendarHeaderModal.zoomWide')}</span>
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.section_label}>{t('scheduler.calendarHeaderModal.visibleHours')}</div>
            <div className={styles.time}>
              <TimeSelect
                label={t('scheduler.calendarHeaderModal.from')}
                slots={startSlots}
                selected={startWorkTime}
                onSelect={handleStartSelect}
              />
              <span className={styles.time_separator} aria-hidden="true">
                –
              </span>
              <TimeSelect
                label={t('scheduler.calendarHeaderModal.to')}
                slots={endSlots}
                selected={displayedEndValue}
                onSelect={handleEndSelect}
              />
            </div>
          </section>
        </>
      )}

      <section className={styles.section}>
        <div className={styles.section_label}>{t('scheduler.calendarHeaderModal.display')}</div>
        <button
          type="button"
          className={clx(styles.toggle_row, {
            [styles.toggle_row_on]: showTaskIcons,
          })}
          onClick={() => onShowTaskIconsChange?.(!showTaskIcons)}
          aria-pressed={showTaskIcons}
        >
          <span className={styles.toggle_copy}>
            <span className={styles.toggle_title}>
              {t('scheduler.calendarHeaderModal.showTaskIcons')}
            </span>
            <span className={styles.toggle_hint}>
              {t('scheduler.calendarHeaderModal.showTaskIconsHint')}
            </span>
          </span>
          <span className={styles.toggle_switch} aria-hidden />
        </button>
        <button
          type="button"
          className={clx(styles.toggle_row, {
            [styles.toggle_row_on]: showTaskTime,
          })}
          onClick={() => onShowTaskTimeChange?.(!showTaskTime)}
          aria-pressed={showTaskTime}
        >
          <span className={styles.toggle_copy}>
            <span className={styles.toggle_title}>
              {t('scheduler.calendarHeaderModal.showTaskTime')}
            </span>
            <span className={styles.toggle_hint}>
              {t('scheduler.calendarHeaderModal.showTaskTimeHint')}
            </span>
          </span>
          <span className={styles.toggle_switch} aria-hidden />
        </button>
      </section>

      <section className={styles.section}>
        <div className={styles.section_label}>{t('scheduler.calendarHeaderModal.closures')}</div>
        <button
          type="button"
          className={clx(styles.toggle_row, {
            [styles.toggle_row_on]: hideClosedChairs,
          })}
          onClick={() => onHideClosedChairsChange?.(!hideClosedChairs)}
          aria-pressed={hideClosedChairs}
        >
          <span className={styles.toggle_copy}>
            <span className={styles.toggle_title}>
              {t('scheduler.calendarHeaderModal.hideClosedChairs')}
            </span>
            <span className={styles.toggle_hint}>
              {t('scheduler.calendarHeaderModal.hideClosedChairsHint')}
            </span>
          </span>
          <span className={styles.toggle_switch} aria-hidden />
        </button>
        <button
          type="button"
          className={clx(styles.toggle_row, {
            [styles.toggle_row_on]: compactClosedDays,
          })}
          onClick={() => onCompactClosedDaysChange?.(!compactClosedDays)}
          aria-pressed={compactClosedDays}
        >
          <span className={styles.toggle_copy}>
            <span className={styles.toggle_title}>
              {t('scheduler.calendarHeaderModal.compactClosedDays')}
            </span>
            <span className={styles.toggle_hint}>
              {t('scheduler.calendarHeaderModal.compactClosedDaysHint')}
            </span>
          </span>
          <span className={styles.toggle_switch} aria-hidden />
        </button>
      </section>
    </div>
  );
};

export default CalendarCustomizeContent;
